import { useInfiniteQuery } from "@tanstack/solid-query";
import type {
  RunDetailResponse,
  RunPage,
  RunPageCursor,
} from "contracts/api/crawl/run-read.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { CRAWL_RUN_STATUS, isActiveCrawlRunStatus } from "contracts/enums/core";
import { For, Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { api } from "../../store/api";
import { RUN_POLL_MS } from "../../store/pollCadence";
import { EmptyState } from "../../ui/EmptyState";
import { relativeAge } from "../../ui/format";
import { PipelineStepper } from "../../ui/PipelineStepper";
import { ScanBreadcrumbs, ScanRouteSurface } from "../scan/ScanRouteChrome";
import { ScanShell } from "../scan/ScanShell";
import { scanCoverage, scanCoverageLine } from "../scan/scanCoverage";
import { contentFitnessHeadline } from "../scan/scanModel";
import { createWebsiteStore } from "../website/store";
import { RunFailureSummary } from "./RunFailureSummary";
import { RunFindingRow } from "./RunFindingRow";
import { RunPagesTable } from "./RunPagesTable";
import {
  runDuration,
  runPartialCoverageSummary,
  runStatusClass,
  runSummaryStatusLabel,
} from "./runPresentation";
import { createAbortRun } from "./runQueries";
import { runPipelineSteps } from "./runStagePresentation";

export function RunDetail(props: { websiteId: string; runId: string }) {
  const context = createWebsiteStore(props.websiteId);
  // Paged on the frontier cursor: the first page carries the run summary and
  // findings, later pages only extend the page list.
  const detail = useInfiniteQuery(() => ({
    queryKey: ["run-detail", props.websiteId, props.runId],
    queryFn: ({ pageParam }) =>
      api.runDetail(props.websiteId, props.runId, pageParam),
    initialPageParam: null as RunPageCursor | null,
    getNextPageParam: (last: RunDetailResponse) => last.nextPageCursor,
    refetchInterval: (current) =>
      isActiveCrawlRunStatus(current.state.data?.pages[0]?.status)
        ? RUN_POLL_MS
        : false,
  }));

  const run = () => detail.data?.pages[0];
  const pages = () => detail.data?.pages.flatMap((page) => page.pages) ?? [];

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.RUNS}>
      <Show
        when={run()}
        keyed
        fallback={<div class="loading signal-loading">Loading run...</div>}
      >
        {(summary) => (
          <RunDetailView
            websiteId={props.websiteId}
            run={summary}
            overview={context.data()}
            pages={pages()}
            hasMore={detail.hasNextPage}
            loadingMore={detail.isFetchingNextPage}
            onLoadMore={() => detail.fetchNextPage()}
          />
        )}
      </Show>
    </ScanShell>
  );
}

function RunDetailView(props: {
  websiteId: string;
  run: RunDetailResponse;
  overview: OverviewResponse | null;
  pages: RunPage[];
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  const run = () => props.run;
  const abort = createAbortRun(props.websiteId);
  const coverage = () =>
    `${run().pageCounts.scraped} pages completed` +
    (run().pageCounts.failed > 0
      ? `, ${run().pageCounts.failed} not completed`
      : "") +
    (run().pageCounts.skipped > 0
      ? `, ${run().pageCounts.skipped} skipped`
      : "");
  const partialCoverage = () => runPartialCoverageSummary(run(), props.pages);
  const currentScoreCoverage = () => {
    const overview = props.overview;
    const scoredRunStartedAt = overview?.lastRun?.startedAt?.getTime();
    if (
      !overview ||
      contentFitnessHeadline(overview).score === null ||
      scoredRunStartedAt !== run().startedAt.getTime()
    ) {
      return null;
    }
    return scanCoverage(overview, run());
  };

  return (
    <ScanRouteSurface class="run-detail">
      <ScanBreadcrumbs
        items={[
          { label: "Runs", href: `/w/${props.websiteId}/runs` },
          { label: run().startedAt.toLocaleString() },
        ]}
      />
      <header class="run-detail-head">
        <span class="signal-mini-kicker">Runs</span>
        <h1 class="scan-title">
          Scan of {run().startedAt.toLocaleString()}{" "}
          <span class={`badge ${runStatusClass(run())}`}>
            {runSummaryStatusLabel(run())}
          </span>
        </h1>
        <p>
          Started {relativeAge(run().startedAt)} · duration {runDuration(run())}
          <Show when={!isActiveCrawlRunStatus(run().status)}>
            {" "}
            · {coverage()}
          </Show>
        </p>
        <PipelineStepper steps={runPipelineSteps(run())} />
        <Show when={run().status === CRAWL_RUN_STATUS.RUNNING}>
          <button
            class="signal-scan-btn"
            type="button"
            onClick={() => abort.mutate(run().runId)}
            disabled={abort.isPending}
          >
            {abort.isPending ? "Cancelling…" : "Cancel run"}
          </button>
          <Show when={abort.error}>
            {(error) => (
              <p class="signal-scan-error" role="alert">
                {error().message}
              </p>
            )}
          </Show>
        </Show>
        <Show when={run().error}>
          {(error) => (
            <div class="signal-scan-error" role="alert">
              <p>The scan did not complete.</p>
              <pre class="run-error-detail">{error()}</pre>
            </div>
          )}
        </Show>
        <Show when={partialCoverage()}>
          {(summary) => <p class="run-coverage-summary">{summary()}</p>}
        </Show>
        <Show when={run().repairedPageCount > 0}>
          <p class="run-repair-summary">
            {run().repairedPageCount} page(s) re-analysed · generation{" "}
            {run().generationCount}.
          </p>
        </Show>
        <Show when={currentScoreCoverage()}>
          {(scoreCoverage) => (
            <p class="run-score-coverage">
              This is the completed run behind the current Content Fitness
              score. {scanCoverageLine(scoreCoverage())}.
            </p>
          )}
        </Show>
      </header>

      <RunFailureSummary pages={props.pages} />

      <RunPagesTable
        websiteId={props.websiteId}
        run={run()}
        pages={props.pages}
        hasMore={props.hasMore}
        loadingMore={props.loadingMore}
        onLoadMore={props.onLoadMore}
      />

      <section>
        <h3>
          Findings{" "}
          <small>
            {run().findingCounts.critical} critical · {run().findingCounts.high}{" "}
            high · {run().findingCounts.medium} medium ·{" "}
            {run().findingCounts.low} low
          </small>
        </h3>
        <Show
          when={run().findings.length > 0}
          fallback={
            <EmptyState
              title="No findings reported"
              hint={
                isActiveCrawlRunStatus(run().status)
                  ? "Findings appear here live as the agent audits each page."
                  : undefined
              }
            />
          }
        >
          <ul class="run-findings">
            <For each={run().findings}>
              {(finding) => <RunFindingRow finding={finding} />}
            </For>
          </ul>
        </Show>
      </section>
    </ScanRouteSurface>
  );
}
